import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Dialog from 'Components/Molecules/Dialog/Dialog';
import { changeProjectWorkspaceSelected } from 'Services/user';
import ItemSelector from 'Components/Molecules/ItemSelector/ItemSelector';
import Loading from 'Components/Atoms/Loading/Loading';
import Icon from 'Components/Atoms/Icon/Icon';
import { useErrors } from 'Components/Organisms/ErrorsProvider';
import { useQueryClient } from '@tanstack/react-query';
import AvatarUser from 'Components/Atoms/AvatarUser/AvatarUser';
import { useUser } from 'Components/Organisms/UserProvider/useUser';
import { useTranslation } from 'react-i18next';
import useProjectWorkspaces from 'Hooks/useProjectWorkspaces';
import IconButton from 'Components/Atoms/IconButtonBare';
import useWorkspaceSelected from 'Hooks/useWorkspaceSelected';
import ChangeWorkspaceCreateForm from './ChangeWorkspaceCreateForm';
import upgradeIconImage from 'assets/upgrade-icon@2x.png';
import usePlansAndPricingDialog from 'Hooks/usePlansAndPricingDialog';
import Button from 'Components/Atoms/Button/Button';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ROUTES } from 'Utils/constants';
import { useSearch } from 'Components/Organisms/SearchProvider';

const wait = (time) => new Promise((resolve) => setTimeout(resolve, time));

export default function ChangeWorkspaceSelectedDialog(props) {
    const { open, onClose, projectId, name, onBack, isOwner } = props;
    const { showUnknownError } = useErrors();
    const queryClient = useQueryClient();
    const [workspaceSelectedId, setWorkspaceSelectedId] = useState(null);
    const { user, refreshUser } = useUser();
    const hasAutoSelected = useRef(false);
    const { workspaces, isLoading } = useProjectWorkspaces(projectId, {
        enabled: !!projectId,
    });

    const params = useParams();
    const navigate = useNavigate();
    const { clearFilters } = useSearch();
    const [searchParams, setSearchParams] = useSearchParams();

    const [showRequestPayment, setShowRequestPayment] = useState(false);
    const { workspace: workspaceSelected, isLoading: isLoadingWorspaceSelected } = useWorkspaceSelected();
    const { openPlansAndPricing } = usePlansAndPricingDialog();

    const { t } = useTranslation('common');

    useEffect(() => {
        if (open) {
            setWorkspaceSelectedId(null);
            setShowRequestPayment(false);
            hasAutoSelected.current = false;
        }
    }, [open]);

    const handleSelectWorkspace = async (workspaceId) => {
        setWorkspaceSelectedId(workspaceId);

        try {
            await changeProjectWorkspaceSelected(workspaceId);
            refreshUser();

            const hasParams = Object.keys(params).length > 0;
            const hasSearchParams = searchParams.size > 0;

            if (hasParams) {
                navigate(ROUTES.settings);
                await wait(300);
            } else if (hasSearchParams) {
                setSearchParams({});
                clearFilters();
            }

            await queryClient.invalidateQueries();
            onClose();
        } catch (error) {
            const hasRequiresPaidError =
                error?.response?.status === 400 && error?.response?.data?.description === 'requires_paid_subscription';
            if (hasRequiresPaidError) {
                setShowRequestPayment(true);
            } else {
                showUnknownError(error);
                onClose();
            }
        } finally {
            setWorkspaceSelectedId(null);
        }
    };

    const handleCreateWorkspace = (workspace) => {
        handleSelectWorkspace(workspace.id);
    };

    // Support users entering another user's project skip the workspace picker:
    // the first workspace of the project is selected automatically.
    useEffect(() => {
        if (!open || !user.is_support || isLoading || isLoadingWorspaceSelected) return;
        if (hasAutoSelected.current || workspaces.length === 0) return;

        const isAlreadyInProject = workspaces.some((w) => w.id === workspaceSelected?.id);
        if (isAlreadyInProject) return;

        hasAutoSelected.current = true;
        handleSelectWorkspace(workspaces[0].id);
    }, [open, user, isLoading, isLoadingWorspaceSelected, workspaces, workspaceSelected]);

    const showLoading = isLoading || isLoadingWorspaceSelected;

    const handleShowPlans = () => {
        openPlansAndPricing();
        onClose();
    };

    if (showRequestPayment) {
        return (
            <Dialog size="sm" open={open} onClose={onClose} zIndex="var(--z-index-popover)">
                <div className="p-2 sm:p-6">
                    <div className="text-center">
                        <div className="mb-6 flex flex-col items-center">
                            <img src={upgradeIconImage} width={84} height={84} alt="" className="mb-4" />
                            <h2 className="mb-2">{t('modal-limit-change-workspace-title')}</h2>
                            <p className="text-gray-500">{t('modal-limit-change-workspace-description')}</p>
                        </div>
                        <Button variant="primary" onClick={handleShowPlans}>
                            {t('see-plans-and-prices')}
                        </Button>
                    </div>
                </div>
            </Dialog>
        );
    }

    return (
        <Dialog open={open} onClose={onClose} size="sm" zIndex="var(--z-index-popover)">
            {onBack && (
                <IconButton
                    onClick={onBack}
                    className="absolute top-5 left-5 text-dark-blue/50 hover:text-dark-blue transition-colors"
                >
                    <Icon size={20} name="back-2" color="currentColor" />
                </IconButton>
            )}
            <div className="p-2 sm:p-6">
                <div className="text-center mb-6">
                    <div className="text-xl font-semibold">{name}</div>
                    <div className="text-gray-500 pb-2">
                        {workspaces?.length === 0 && isOwner
                            ? t('select-workspace.create-workspace')
                            : t('select-workspace.select-workspace')}
                    </div>
                </div>

                {showLoading && (
                    <div className="py-6">
                        <Loading message={t('select-workspace.workspaces-loading')} />
                    </div>
                )}

                {!showLoading && (
                    <>
                        <div className="grid gap-4" style={{ pointerEvents: workspaceSelectedId ? 'none' : 'all' }}>
                            {workspaces.map((workspace) => (
                                <ItemSelector
                                    customImage={<AvatarUser name={workspace.name} size={50} />}
                                    isBusy={workspaceSelectedId === workspace.id}
                                    key={workspace.id}
                                    title={workspace.name}
                                    onClick={() => handleSelectWorkspace(workspace.id)}
                                    icon={
                                        workspaceSelected.id === workspace.id ? (
                                            <Icon name="circle-check" size={32} color="#74B62C" />
                                        ) : (
                                            <div />
                                        )
                                    }
                                />
                            ))}
                        </div>

                        {workspaces.length === 0 && isOwner && (
                            <ChangeWorkspaceCreateForm
                                onError={onClose}
                                onCreate={handleCreateWorkspace}
                                projectId={projectId}
                            />
                        )}

                        {workspaces.length === 0 && !isOwner && (
                            <div className="text-center">
                                <div className="text-gray-500 mb-4">{t('select-workspace.owner-not-assigned')}</div>

                                <Button variant="primary" onClick={onClose}>
                                    {t('select-workspace.accept')}
                                </Button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </Dialog>
    );
}

ChangeWorkspaceSelectedDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onBack: PropTypes.func.isRequired,
    projectId: PropTypes.string,
    name: PropTypes.string,
    isOwner: PropTypes.bool,
};
